import React, { useEffect, useState, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { Link } from 'react-router-dom'
import { collection, query, where, getDocs, doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore'
import { db, functions } from '../firebaseConfig'
import { httpsCallable } from 'firebase/functions'
import EmptyState from '../assets/images/EmptyState.png'
import Select from 'react-select'
import Modal from './Modal'
import { toast } from 'react-toastify'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import BsTimerDisplay from './bsTimerDisplay'
import { useTheme } from '../context/ThemeContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faEnvelope, faFileCircleCheck, faMoneyBillWave, faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { generateBsPDF } from '../utils/BsPdf'

// Bagian AQ: status yang disembunyikan dari list milik user sendiri (lihat
// catatan di filterOptions.status di bawah).
const HIDDEN_STATUSES = ['Ditolak', 'Dibatalkan']

const BsTable = () => {
    const { theme } = useTheme()
    const [data, setData] = useState({ bonSementara: [] })
    const [loading, setLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const [lpjStatus, setLpjStatus] = useState({})

    // --- Kirim Reminder ke Finance (Validator) untuk BS berstatus Disetujui ---
    // submitterUnits: semua Unit Bisnis yang terdaftar di profil user yang login
    // (bisa lebih dari satu). financeValidators: semua user role Validator
    // (dari /userDirectory, bukan /users -- lihat Bagian K/21.3) beserta unit
    // penempatannya, dipakai mencari Finance yang match dengan submitterUnits.
    const [submitterUnits, setSubmitterUnits] = useState([])
    const [financeValidators, setFinanceValidators] = useState([])
    const [reminderSendingId, setReminderSendingId] = useState(null)
    const [financePickerModal, setFinancePickerModal] = useState(null) // { item, candidates }

    // Get current date
    const today = new Date()
    const currentYear = today.getFullYear()
    const currentMonth = today.getMonth() + 1 // JavaScript months are 0-indexed

    const [yearOptions, setYearOptions] = useState([{ value: currentYear, label: `${currentYear}` }])

    // Set default filters with current month and year
    const [filters, setFilters] = useState({
        status: '',
        kategori: '',
        bulan: { value: currentMonth, label: new Intl.DateTimeFormat('id-ID', { month: 'long' }).format(today) },
        tahun: { value: currentYear, label: `${currentYear}` }
    })
    const itemsPerPage = 5 // Jumlah item per halaman

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedReport, setSelectedReport] = useState(null)
    const [cancelReason, setCancelReason] = useState('')

    // Bagian AQ: pengajuan Ditolak/Dibatalkan (apa pun pelakunya -- user
    // sendiri, validator, atau reviewer) sengaja TIDAK ditampilkan lagi di
    // list milik user sendiri -- keduanya jalan buntu tanpa aksi lanjutan,
    // cuma menumpuk sebagai noise. Riwayatnya tetap utuh & tetap bisa dilihat
    // Super Admin lewat "Cek Bon Sementara" (tabel Semua Status) dan "Ekspor
    // Laporan Pengajuan" -- TIDAK dihapus dari Firestore, cuma disembunyikan
    // dari tampilan list ini.
    const filterOptions = {
        status: [
            { value: 'Diajukan', label: 'Diajukan' },
            { value: 'Diproses', label: 'Diproses' },
            { value: 'Disetujui', label: 'Disetujui' }
        ],
        kategori: [
            { value: 'Marketing/Operasional', label: 'Marketing/Operasional' },
            { value: 'GA/Umum', label: 'GA/Umum' }
        ],
        bulan: [
            { value: 1, label: 'Januari' },
            { value: 2, label: 'Februari' },
            { value: 3, label: 'Maret' },
            { value: 4, label: 'April' },
            { value: 5, label: 'Mei' },
            { value: 6, label: 'Juni' },
            { value: 7, label: 'Juli' },
            { value: 8, label: 'Agustus' },
            { value: 9, label: 'September' },
            { value: 10, label: 'Oktober' },
            { value: 11, label: 'November' },
            { value: 12, label: 'Desember' }
        ]
    }

    // Bagian BA: dipisah ke useCallback (bukan cuma fungsi lokal di dalam
    // useEffect) supaya bisa dipanggil ulang dari effect visibilitychange di
    // bawah -- sebelumnya cuma fetch SEKALI saat mount, jadi kolom "Status
    // LPJ" bisa BEKU/basi kalau tab ini dibiarkan terbuka lama (mis. LPJ-nya
    // baru saja divalidasi validator di sesi lain, tapi tab ini tidak pernah
    // tahu sampai di-reload manual) -- persis pola yang bikin bingung "saya
    // sedang LPJ-kan tapi statusnya masih Belum LPJ".
    const fetchUserAndBonSementara = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true)
        try {
            const uid = localStorage.getItem('userUid')
            if (!uid) {
                console.error('UID tidak ditemukan di localStorage')
                setLoading(false)
                return
            }

            // Query bon sementara berdasarkan UID user
            const q = query(
                collection(db, 'bonSementara'),
                where('user.uid', '==', uid) // Filter data bon sementara berdasarkan UID user
            )

            const querySnapshot = await getDocs(q)
            const bonSementara = querySnapshot.docs
                .map((doc) => ({
                    id: doc.id,
                    displayId: doc.data().displayId,
                    ...doc.data()
                }))
                .filter((item) => !HIDDEN_STATUSES.includes(item.status))

            const existingYears = new Set(bonSementara.map((item) => new Date(item.tanggalPengajuan).getFullYear()))

            const updatedYearOptions = Array.from(existingYears)
                .map((year) => ({ value: year, label: `${year}` }))
                .sort((a, b) => b.value - a.value) // Urutkan tahun dari yang terbaru

            setYearOptions(updatedYearOptions)
            setData({ bonSementara })
        } catch (error) {
            console.error('Error fetching user or bon sementara data:', error)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchUserAndBonSementara()
    }, [fetchUserAndBonSementara])

    // Bagian BA: begitu tab ini dibuka lagi setelah sempat di-background
    // (pindah tab lain / minimize / pindah aplikasi lalu balik) -- refresh
    // diam-diam (tanpa skeleton loading, `silent: true`) supaya kolom "Status
    // LPJ" & data lain ikut update kalau ada perubahan dari sesi lain
    // (validator approve, LPJ baru disubmit, dst) selama tab ini idle.
    // Throttle minimal 60 detik antar refresh supaya tidak query berulang
    // kalau user gonta-ganti tab dengan cepat.
    useEffect(() => {
        let lastFetchAt = Date.now()

        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return
            if (Date.now() - lastFetchAt < 60000) return
            lastFetchAt = Date.now()
            fetchUserAndBonSementara({ silent: true })
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
    }, [fetchUserAndBonSementara])

    const fetchLpjStatus = async (bonSementaraList) => {
        try {
            const uid = localStorage.getItem('userUid')
            if (!uid) return

            // Ambil SEMUA lpj milik user sekali saja (query ini difilter user.uid, jadi bisa
            // dibuktikan oleh Firestore Security Rules - sama seperti query bonSementara di
            // atas). Query per-BS yang lama (filter hanya by nomorBS, tanpa user.uid) ditolak
            // Firestore karena tidak bisa dibuktikan aman untuk operasi list/query.
            const lpjSnapshot = await getDocs(
                query(collection(db, 'lpj'), where('user.uid', '==', uid))
            )
            // Bagian BB: field "Nomor Bon Sementara" di form LPJ (FormLpjUmum.jsx/
            // FormLpjMarketing.jsx) dulu input teks bebas -- rawan beda spasi/
            // kapitalisasi dari `nomorBS`/`displayId` BS aslinya walau terlihat
            // identik di layar. Normalisasi (trim + uppercase) SEBELUM
            // dicocokkan supaya kasus seperti itu tetap ketemu.
            const normalizeNomorBS = (value) => (value || '').toString().trim().toUpperCase()

            // Bagian BE: SATU BS bisa punya LEBIH DARI SATU dokumen LPJ dengan
            // nomorBS yang PERSIS SAMA -- kasus nyata: LPJ pertama dibatalkan
            // (Dibatalkan), lalu user mengajukan LPJ BARU untuk BS yang sama.
            // Dictionary lama meng-overwrite entry tanpa peduli urutan/prioritas,
            // jadi kalau dokumen "Dibatalkan" kebetulan diproses BELAKANGAN oleh
            // Firestore (urutan query tidak dijamin), entry LPJ yang AKTIF
            // (mis. "Divalidasi") malah tertimpa jadi terlihat "tidak ada LPJ"
            // -- BS tampil "Belum LPJ" selamanya walau LPJ aktifnya sudah jalan.
            // Prioritaskan status LPJ paling relevan: Disetujui > status aktif
            // lainnya (Diajukan/Diproses/Divalidasi/dst) > Dibatalkan/Ditolak.
            const lpjStatusPriority = (status) => {
                if (status === 'Disetujui') return 3
                if (status === 'Dibatalkan' || status === 'Ditolak') return 1
                return 2
            }
            const lpjTimestamp = (d) => {
                const value = d.tanggalPengajuan || d.createdAt
                const parsed = value ? new Date(value).getTime() : NaN
                return isNaN(parsed) ? 0 : parsed
            }

            const lpjByNomorBS = {}
            lpjSnapshot.docs.forEach((docSnap) => {
                const d = docSnap.data()
                const key = normalizeNomorBS(d.nomorBS)
                if (!key) return
                const existing = lpjByNomorBS[key]
                if (!existing) {
                    lpjByNomorBS[key] = d
                    return
                }
                const newPriority = lpjStatusPriority(d.status)
                const existingPriority = lpjStatusPriority(existing.status)
                if (newPriority > existingPriority) {
                    lpjByNomorBS[key] = d
                } else if (newPriority === existingPriority && lpjTimestamp(d) > lpjTimestamp(existing)) {
                    lpjByNomorBS[key] = d
                }
            })

            const newLpjStatus = {};

            // Bagian CE: koreksi dari user -- satu dokumen BS bisa punya LEBIH
            // DARI SATU item (nomorBS/jumlahBS berbeda-beda, lihat FormBs.jsx
            // `multipleItems`), tapi sebelumnya cuma `bs.displayId` (=
            // bonSementara[0].nomorBS) yang dicocokkan ke LPJ -- item ke-2 dst
            // (mis. nomorBS "...508" dalam dokumen yang displayId-nya "...507")
            // tidak pernah terdeteksi LPJ-nya sama sekali. Sekarang SEMUA item
            // dicek, status gabungan: "Sudah LPJ" kalau SEMUA item sudah,
            // "Sedang LPJ" kalau ADA yang aktif diproses, selain itu "Belum LPJ".
            for (const bs of bonSementaraList) {
                const items = bs.bonSementara?.length ? bs.bonSementara : [{ nomorBS: bs.displayId }]
                let hasBelum = false
                let hasSedang = false
                let hasSudah = false
                let representativeLpjData = null

                items.forEach((it) => {
                    const lpjData = lpjByNomorBS[normalizeNomorBS(it.nomorBS)]
                    if (!lpjData || lpjData.status === 'Dibatalkan' || lpjData.status === 'Ditolak') {
                        hasBelum = true
                    } else if (lpjData.status === 'Disetujui') {
                        hasSudah = true
                        if (!representativeLpjData) representativeLpjData = lpjData
                    } else {
                        hasSedang = true
                        representativeLpjData = lpjData
                    }
                })

                if (hasSedang) {
                    newLpjStatus[bs.id] = { status: 'Sedang LPJ', statusHistory: representativeLpjData?.statusHistory || [] }
                } else if (hasBelum) {
                    // Sebagian/semua item belum LPJ -- tetap "Belum LPJ" supaya
                    // tombol reminder & timer tetap muncul untuk item yang
                    // belum dilaporkan, meski item lain sudah.
                    newLpjStatus[bs.id] = { status: 'Belum LPJ', statusHistory: representativeLpjData?.statusHistory || [] }
                } else if (hasSudah) {
                    newLpjStatus[bs.id] = { status: 'Sudah LPJ', statusHistory: representativeLpjData?.statusHistory || [] }
                } else {
                    newLpjStatus[bs.id] = { status: 'Belum LPJ' }
                }
            }

            setLpjStatus(newLpjStatus);
        } catch (error) {
            console.error('Error fetching LPJ statuses:', error);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A' // Handle null/undefined
        const date = new Date(dateString)
        return new Intl.DateTimeFormat('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).format(date)
    }

    const handleFilterChange = (field, selectedOption) => {
        setFilters((prev) => ({
            ...prev,
            [field]: selectedOption
        }))
        setCurrentPage(1)
    }

    const filteredBonSementara = data.bonSementara
        .filter((item) => {
            const matchesStatus = filters.status ? item.status === filters.status.value : true

            const matchesCategory = filters.kategori ? item.bonSementara[0].kategori === filters.kategori.value : true

            const matchesMonth = filters.bulan
                ? new Date(item.tanggalPengajuan).getMonth() + 1 === filters.bulan.value
                : true

            const matchesYear = filters.tahun
                ? new Date(item.tanggalPengajuan).getFullYear() === filters.tahun.value
                : true

            return matchesStatus && matchesCategory && matchesMonth && matchesYear
        })

        // Urutkan dari tanggal terbaru ke terlama
        .sort((a, b) => new Date(b.tanggalPengajuan) - new Date(a.tanggalPengajuan))

    const totalPages = Math.ceil(filteredBonSementara.length / itemsPerPage)
    const currentBonSementara = filteredBonSementara.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

    // Add this useEffect after your existing useEffect
    useEffect(() => {
        if (data.bonSementara.length > 0) {
            fetchLpjStatus(data.bonSementara);
        }
    }, [data.bonSementara]);

    // Data pendukung untuk tombol "Kirim Reminder ke Finance" -- unit bisnis
    // milik user yang login (self-read, selalu diizinkan) & daftar semua
    // Validator lintas user (dari /userDirectory, aman dibaca siapa saja login).
    useEffect(() => {
        const fetchFinanceReminderData = async () => {
            try {
                const uid = localStorage.getItem('userUid')
                if (!uid) return

                const userDoc = await getDoc(doc(db, 'users', uid))
                setSubmitterUnits(Array.isArray(userDoc.data()?.unit) ? userDoc.data().unit : [])

                const validatorSnapshot = await getDocs(
                    query(collection(db, 'userDirectory'), where('role', '==', 'Validator'))
                )
                setFinanceValidators(
                    validatorSnapshot.docs.map((docSnap) => ({
                        uid: docSnap.id,
                        nama: docSnap.data().nama || 'Validator',
                        unit: Array.isArray(docSnap.data().unit) ? docSnap.data().unit : []
                    }))
                )
            } catch (error) {
                console.error('Error fetching data reminder finance:', error)
            }
        }

        fetchFinanceReminderData()
    }, []);

    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1)
        }
    }

    // Fungsi untuk berpindah ke halaman sebelumnya
    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1)
        }
    }

    const handleCancel = (report) => {
        setSelectedReport(report)
        setIsModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setCancelReason('')
        setSelectedReport(null)
    }

    const handleSubmitCancel = async () => {
        if (!selectedReport || !cancelReason) {
            toast.warning('Harap isi alasan pembatalan terlebih dahulu!')
            return
        }

        try {
            const uid = localStorage.getItem('userUid')
            const bonSemetaraDocRef = doc(db, 'bonSementara', selectedReport.id)

            const newStatusHistory = {
                timestamp: new Date().toISOString(),
                actor: uid,
                status: 'Dibatalkan'
            };

            // Memperbarui data di Firestore
            await updateDoc(bonSemetaraDocRef, {
                status: 'Dibatalkan',
                cancelReason: cancelReason || 'Alasan tidak diberikan',
                statusHistory: arrayUnion(newStatusHistory)
            })

            // Refresh data
            const q = query(collection(db, 'bonSementara'), where('user.uid', '==', uid))
            const querySnapshot = await getDocs(q)
            const bonSementara = querySnapshot.docs
                .map((doc) => ({
                    id: doc.id,
                    displayId: doc.data().displayId,
                    ...doc.data()
                }))
                .filter((item) => !HIDDEN_STATUSES.includes(item.status))

            setData({ bonSementara }) // Mengupdate data baru -- item yang baru dibatalkan otomatis hilang dari list (lihat HIDDEN_STATUSES)

            toast.success('Pengajuan Bon Sementara berhasil dibatalkan.')
            // Menutup modal setelah pembatalan
            handleCloseModal()
        } catch (error) {
            console.error('Error cancelling bon sementara:', error?.code, error?.message, error)
            toast.error('Gagal membatalkan bon sementara. Silakan coba lagi.')
        }
    }

    const sendFinanceReminder = async (item, validator) => {
        setReminderSendingId(item.id)
        try {
            const pdfUrl = await generateBsPDF(item)
            if (!pdfUrl) {
                toast.error('Gagal membuat PDF BS untuk lampiran reminder')
                return
            }

            const sendBsFinanceReminder = httpsCallable(functions, 'sendBsFinanceReminder')
            await sendBsFinanceReminder({ bsId: item.id, validatorUid: validator.uid, pdfUrl })

            toast.success(`Reminder terkirim ke ${validator.nama}`)
        } catch (error) {
            console.error('Error sending finance reminder:', error)
            toast.error(error?.message || 'Gagal mengirim reminder ke Finance')
        } finally {
            setReminderSendingId(null)
        }
    }

    const handleSendFinanceReminder = (item) => {
        const candidates = financeValidators.filter((validator) =>
            validator.unit.some((unit) => submitterUnits.includes(unit))
        )

        if (candidates.length === 0) {
            toast.error('Tidak ada Finance (Validator) yang terdaftar untuk Unit Bisnis Anda. Hubungi Admin.')
            return
        }

        // Cuma 1 perusahaan terdaftar di profil pengaju -> tidak ada pilihan yang
        // perlu diambil, langsung kirim ke semua Finance yang match unit tsb.
        if (submitterUnits.length <= 1) {
            candidates.forEach((validator) => sendFinanceReminder(item, validator))
            return
        }

        // Terdaftar di lebih dari 1 perusahaan -> minta pilih salah satu Finance
        setFinancePickerModal({ item, candidates })
    }

    const handlePickFinance = (validator) => {
        if (!financePickerModal) return
        const { item } = financePickerModal
        setFinancePickerModal(null)
        sendFinanceReminder(item, validator)
    }

    // Bagian CB: tombol "Transferred" untuk BS -- sebelumnya cuma ada di
    // halaman "Mutasi Dana" (dihapus, prosesnya dianggap user kurang jelas).
    // Dipindah ke sini, pola identik `handleMarkTransferred` di
    // ReimbursementTable.jsx -- field `transferred`/`transferredAt` yang
    // sama (lihat canMarkTransferred() di firestore.rules, sudah generik
    // untuk bonSementara/reimbursement/lpj).
    const handleMarkTransferred = async (item) => {
        try {
            const bsDocRef = doc(db, 'bonSementara', item.id)
            const transferredAt = new Date().toISOString()
            await updateDoc(bsDocRef, { transferred: true, transferredAt })
            setData((prev) => ({
                bonSementara: prev.bonSementara.map((b) =>
                    b.id === item.id ? { ...b, transferred: true, transferredAt } : b
                )
            }))
            toast.success('Ditandai sebagai sudah masuk')
        } catch (error) {
            console.error('Error marking transferred:', error)
            toast.error('Gagal menandai sebagai Transferred')
        }
    }

    const shouldShowAlert = (item) => {
        // Check if the BS is approved
        if (item.status !== 'Disetujui') return false

        // Find approval entry in statusHistory
        const approvalEntry =
            item.statusHistory &&
            item.statusHistory.find(
                (entry) =>
                    entry.status &&
                    (entry.status === 'Disetujui oleh Reviewer 2' ||
                        entry.status === 'Disetujui oleh Super Admin (Pengganti Reviewer 2)')
            )

        if (!approvalEntry) return false

        const approvalDate = new Date(approvalEntry.timestamp)
        const currentDate = new Date()

        // Calculate days difference
        const diffTime = currentDate - approvalDate
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

        // Show alert if more than 3 days have passed and LPJ status is "Belum LPJ" or "Sedang LPJ"
        return diffDays >= 3 && lpjStatus[item.id] &&
            (lpjStatus[item.id].status === 'Belum LPJ' || lpjStatus[item.id].status === 'Sedang LPJ');
    }

    const shouldShowTimer = (item) => {
        // Check if the BS is approved
        if (item.status !== 'Disetujui') return false;

        // Find approval entry in statusHistory
        const approvalEntry =
            item.statusHistory &&
            item.statusHistory.find(
                (entry) =>
                    entry.status &&
                    (entry.status === 'Disetujui oleh Reviewer 2' ||
                        entry.status === 'Disetujui oleh Super Admin (Pengganti Reviewer 2)')
            );

        if (!approvalEntry) return false;

        // Show timer if BS is approved and LPJ status is any of these three states
        return lpjStatus[item.id] &&
            (lpjStatus[item.id].status === 'Belum LPJ' ||
                lpjStatus[item.id].status === 'Sedang LPJ' ||
                lpjStatus[item.id].status === 'Sudah LPJ');
    };

    const isDark = theme === 'dark'
    const selectStyles = {
        control: (base) => ({
            ...base,
            display: 'flex', // Menggunakan Flexbox
            alignItems: 'center', // Teks berada di tengah vertikal
            justifyContent: 'space-between', // Menjaga ikon dropdown di kanan
            backgroundColor: isDark ? '#1f2937' : '#ffffff',
            borderColor: isDark ? '#4b5563' : '#e5e7eb',
            fontSize: '12px', // Ukuran teks
            height: '32px', // Tinggi field tetap
            padding: '0 4px', // Padding horizontal
            lineHeight: 'normal', // Pastikan line-height default
            '&:hover': {
                borderColor: '#3b82f6'
            },
            borderRadius: '8px' // Sudut melengkung
        }),
        singleValue: (base) => ({ ...base, color: isDark ? '#f3f4f6' : '#111827' }),
        input: (base) => ({ ...base, color: isDark ? '#f3f4f6' : '#111827' }),
        placeholder: (base) => ({ ...base, color: isDark ? '#9ca3af' : '#6b7280' }),
        menu: (base) => ({
            ...base,
            zIndex: 100,
            backgroundColor: isDark ? '#1f2937' : '#ffffff'
        }),
        // Bagian AX: menuPortalTarget={document.body} butuh z-index tinggi di
        // sini juga (bukan cuma di `menu`) supaya dropdown tidak tampil
        // tertutup/salah posisi kalau Select ini dirender di dalam modal lain.
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (base, state) => ({
            ...base,
            fontSize: '12px',
            padding: '6px 12px',
            cursor: 'pointer',
            backgroundColor: isDark
                ? (state.isSelected ? '#374151' : state.isFocused ? '#2d3748' : '#1f2937')
                : base.backgroundColor,
            color: isDark ? '#f3f4f6' : base.color
        })
    }

    const FilterSelect = ({ field, label }) => {
        // For year, use the dynamically generated yearOptions
        const options = field === 'tahun' ? yearOptions : filterOptions[field]

        return (
            <Select
                value={filters[field]}
                onChange={(option) => handleFilterChange(field, option)}
                options={options}
                placeholder={label}
                isClearable={field !== 'bulan' && field !== 'tahun'}
                className="w-38 lg:w-40"
                styles={selectStyles}
                isSearchable={false}
                menuPortalTarget={document.body}
                menuPosition="absolute"
            />
        )
    }

    if (loading) {
        return (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg mb-6 shadow-sm transition-colors">
                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between mb-2 gap-4">
                    <h3 className="text-xl font-medium dark:text-gray-100">Bon Sementara Diajukan</h3>
                    <div className="grid grid-cols-2 lg:flex lg:flex-row gap-2">
                        {[...Array(4)].map((_, index) => (
                            <div key={index} className="w-full lg:w-40">
                                <Skeleton width="100%" height={32} />
                            </div>
                        ))}
                    </div>
                </div>
                <Skeleton count={5} height={40} />
            </div>
        )
    }

    const shouldShowEmptyState = data.bonSementara.length === 0 || filteredBonSementara.length === 0

    return (
        <div>
            {shouldShowEmptyState ? (
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg mb-6 shadow-sm transition-colors">
                    <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between mb-2 gap-4">
                        <h3 className="text-xl font-medium items-center dark:text-gray-100">Bon Sementara Diajukan</h3>
                        <div className="grid grid-cols-2 lg:flex lg:flex-row gap-2">
                            <FilterSelect field="status" label="Status" />
                            <FilterSelect field="kategori" label="Kategori" />
                            <FilterSelect field="bulan" label="Bulan" />
                            <FilterSelect field="tahun" label="Tahun" />
                        </div>
                    </div>
                    <div className="flex flex-col items-center justify-center mt-4">
                        <figure className="w-44 h-44 mb-4">
                            <img src={EmptyState} alt="bon sementara icon" className="w-full h-full object-contain" />
                        </figure>
                    </div>
                </div>
            ) : (
                // Jika ada data bon sementara
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg mb-6 shadow-sm transition-colors">
                    <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between mb-2 gap-4">
                        <h3 className="text-xl font-medium items-center dark:text-gray-100">Bon Sementara Diajukan</h3>
                        <div className="grid grid-cols-2 lg:flex lg:flex-row gap-2">
                            <FilterSelect field="status" label="Status" />
                            <FilterSelect field="kategori" label="Kategori" />
                            <FilterSelect field="bulan" label="Bulan" />
                            <FilterSelect field="tahun" label="Tahun" />
                        </div>
                    </div>

                    <div className="w-full">
                        <div className="w-full overflow-x-auto">
                            <div className="inline-block min-w-[1000px] w-full">
                                <table className="w-full bg-white dark:bg-gray-800 text-sm dark:text-gray-200">
                                    <thead>
                                        <tr className="bg-gray-100 dark:bg-gray-700 text-left dark:text-gray-100">
                                            <th className="px-2 py-2 border dark:border-gray-600 text-center w-auto">No.</th>
                                            <th className="px-4 py-2 border dark:border-gray-600">Nomor BS</th>
                                            <th className="px-4 py-2 border dark:border-gray-600">Kategori BS</th>
                                            <th className="px-4 py-2 border dark:border-gray-600">Jumlah BS</th>
                                            <th className="px-4 py-2 border dark:border-gray-600">Tanggal Pengajuan</th>
                                            <th className="py-2 border dark:border-gray-600 text-center">Status</th>
                                            <th className="py-2 border dark:border-gray-600 text-center">Status LPJ</th>
                                            <th className="py-2 border dark:border-gray-600 text-center">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentBonSementara.map((item, index) => (
                                            <tr key={index}>
                                                <td className="px-2 py-2 border dark:border-gray-600 text-center w-auto">
                                                    {index + 1 + (currentPage - 1) * itemsPerPage}
                                                </td>
                                                <td className="px-4 py-2 border dark:border-gray-600">
                                                    <div className="flex items-center gap-2">
                                                        {lpjStatus[item.id]?.status === 'Sudah LPJ' && (
                                                            <span className="text-gray-500 dark:text-gray-400" title="Reported">
                                                                <FontAwesomeIcon icon={faFileCircleCheck} className="w-3 h-3" />
                                                            </span>
                                                        )}
                                                        <Link
                                                            to={`/bon-sementara/${item.id}`}
                                                            className="text-black dark:text-gray-100 hover:text-gray-700 dark:hover:text-gray-300 hover:underline cursor-pointer"
                                                        >
                                                            {item.displayId}
                                                        </Link>
                                                        {shouldShowAlert(item) && (
                                                            <span className="text-red-600" title="LPJ belum diselesaikan setelah 3 hari persetujuan">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                                </svg>
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2 border dark:border-gray-600">{item.bonSementara[0].kategori}</td>
                                                <td className="px-4 py-2 border dark:border-gray-600">
                                                    Rp{item.bonSementara[0].jumlahBS.toLocaleString('id-ID')}
                                                </td>
                                                <td className="px-4 py-2 border dark:border-gray-600">
                                                    {formatDate(item.tanggalPengajuan)}
                                                </td>
                                                <td className="px-2 py-2 border text-center">
                                                    <span
                                                        className={`px-4 py-1 rounded-full text-xs font-medium 
                                            ${item.status === 'Diajukan'
                                                                ? 'bg-blue-200 text-blue-800 border-[1px] border-blue-600'
                                                                : item.status === 'Disetujui'
                                                                    ? 'bg-green-200 text-green-800 border-[1px] border-green-600'
                                                                    : item.status === 'Diproses'
                                                                        ? 'bg-yellow-200 text-yellow-800 border-[1px] border-yellow-600'
                                                                        : item.status === 'Ditolak'
                                                                            ? 'bg-red-200 text-red-800 border-[1px] border-red-600'
                                                                            : 'bg-gray-300 text-gray-700 border-[1px] border-gray-600'
                                                            }`}
                                                    >
                                                        {item.status || 'Tidak Diketahui'}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-2 border text-center">
                                                    <div className="flex flex-col items-center gap-2">
                                                        {lpjStatus[item.id] ? (
                                                            <>
                                                                {(item.status !== 'Disetujui') && (
                                                                    <span className="px-4 py-1 rounded-full text-xs font-medium 
                        bg-gray-300 text-gray-700 border-[1px] border-gray-600">
                                                                        {item.status === 'Diajukan' || item.status === 'Diproses'
                                                                            ? 'Menunggu Persetujuan'
                                                                            : item.status === 'Ditolak' || item.status === 'Dibatalkan'
                                                                                ? 'Tidak Dapat LPJ'
                                                                                : ''}
                                                                    </span>
                                                                )}
                                                                {shouldShowTimer(item) && (
                                                                    <BsTimerDisplay
                                                                        approvalDate={item.statusHistory.find(entry =>
                                                                            entry.status === 'Disetujui oleh Reviewer 2' ||
                                                                            entry.status === 'Disetujui oleh Super Admin (Pengganti Reviewer 2)'
                                                                        )?.timestamp}
                                                                        lpjStatus={lpjStatus[item.id].status}
                                                                        lpjStatusHistory={lpjStatus[item.id].statusHistory}
                                                                    />
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span className="px-4 py-1 rounded-full text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-[1px] border-gray-600 dark:border-gray-500">
                                                                Loading...
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-2 py-2 border text-center">
                                                    {item.status === 'Disetujui' ? (
                                                        <div className="inline-flex items-center justify-center gap-3">
                                                            {item.transferred ? (
                                                                <span
                                                                    className="inline-flex items-center justify-center text-gray-400 dark:text-gray-500 cursor-not-allowed"
                                                                    title="Transferred"
                                                                >
                                                                    <FontAwesomeIcon icon={faCheckCircle} />
                                                                </span>
                                                            ) : (
                                                                <button
                                                                    className="inline-flex items-center justify-center text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                                                    onClick={() => handleMarkTransferred(item)}
                                                                    title="Transferred"
                                                                >
                                                                    <FontAwesomeIcon icon={faMoneyBillWave} />
                                                                </button>
                                                            )}
                                                            {item.transferred ? (
                                                                // Bagian CF: koreksi dari user -- begitu dana sudah
                                                                // dikonfirmasi masuk (Transferred), reminder ke Finance
                                                                // tidak relevan lagi, sama seperti pola RBS
                                                                // (ReimbursementTable.jsx) & LpjBsTable.jsx (Bagian CD).
                                                                <span className="text-gray-400 dark:text-gray-500" title="Transferred -- reminder tidak diperlukan">-</span>
                                                            ) : lpjStatus[item.id]?.status === 'Sudah LPJ' ? (
                                                                // Bagian BQ: BS sudah Reported (LPJ-nya Disetujui) -> urusan
                                                                // pencairan BS sudah selesai, reminder ke Finance tidak relevan.
                                                                <span className="text-gray-400 dark:text-gray-500" title="Reported -- reminder tidak diperlukan">-</span>
                                                            ) : (
                                                                <button
                                                                    className="inline-flex items-center justify-center text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    onClick={() => handleSendFinanceReminder(item)}
                                                                    disabled={reminderSendingId === item.id}
                                                                    title="Send Reminder to Finance"
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={reminderSendingId === item.id ? faSpinner : faEnvelope}
                                                                        className={reminderSendingId === item.id ? 'animate-spin' : ''}
                                                                    />
                                                                </button>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <button
                                                            className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 disabled:text-gray-400 dark:disabled:text-gray-600 disabled:cursor-not-allowed hover"
                                                            onClick={() => handleCancel(item)}
                                                            disabled={item.status !== 'Diajukan'}
                                                        >
                                                            Batalkan
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Conditional Pagination - hanya muncul jika lebih dari satu page */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-1 mt-6 text-xs">
                            {/* Tombol Previous */}
                            <button
                                onClick={prevPage}
                                disabled={currentPage === 1}
                                className={`flex items-center px-2 h-9 rounded-full ${currentPage === 1
                                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                                    : 'border border-red-600 dark:border-red-500 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                                    }`}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="size-4"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M15.75 19.5L8.25 12l7.5-7.5"
                                    />
                                </svg>
                            </button>

                            {/* Tombol Halaman dengan Ellipsis */}
                            {(() => {
                                let pages = []
                                // Mengurangi jumlah halaman yang ditampilkan di mobile
                                const visiblePages = window.innerWidth < 640 ? 1 : 3

                                // Selalu tampilkan halaman pertama
                                pages.push(
                                    <button
                                        key={1}
                                        onClick={() => setCurrentPage(1)}
                                        className={`min-w-[36px] h-9 rounded-full ${currentPage === 1
                                            ? 'bg-red-600 text-white'
                                            : 'border border-red-600 dark:border-red-500 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                                            }`}
                                    >
                                        1
                                    </button>
                                )

                                if (totalPages <= visiblePages + 2) {
                                    // Jika total halaman sedikit, tampilkan semua
                                    for (let i = 2; i <= totalPages; i++) {
                                        pages.push(
                                            <button
                                                key={i}
                                                onClick={() => setCurrentPage(i)}
                                                className={`min-w-[36px] h-9 rounded-full ${currentPage === i
                                                    ? 'bg-red-600 text-white'
                                                    : 'border border-red-600 dark:border-red-500 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                                                    }`}
                                            >
                                                {i}
                                            </button>
                                        )
                                    }
                                } else {
                                    // Logika untuk mobile view
                                    if (window.innerWidth < 640) {
                                        // Jika current page bukan di awal atau akhir, tampilkan ellipsis di kedua sisi
                                        if (currentPage > 2 && currentPage < totalPages - 1) {
                                            pages.push(
                                                <span key="ellipsis1" className="px-1">
                                                    ...
                                                </span>
                                            )
                                            pages.push(
                                                <button
                                                    key={currentPage}
                                                    onClick={() => setCurrentPage(currentPage)}
                                                    className="min-w-[36px] h-9 rounded-full bg-red-600 text-white"
                                                >
                                                    {currentPage}
                                                </button>
                                            )
                                            pages.push(
                                                <span key="ellipsis2" className="px-1">
                                                    ...
                                                </span>
                                            )
                                        } else if (currentPage <= 2) {
                                            // Tampilkan halaman 2 jika current page di awal
                                            if (currentPage === 2) {
                                                pages.push(
                                                    <button
                                                        key={2}
                                                        onClick={() => setCurrentPage(2)}
                                                        className="min-w-[36px] h-9 rounded-full bg-red-600 text-white"
                                                    >
                                                        2
                                                    </button>
                                                )
                                            }
                                            pages.push(
                                                <span key="ellipsis1" className="px-1">
                                                    ...
                                                </span>
                                            )
                                        } else {
                                            // Tampilkan ellipsis dan halaman sebelum terakhir jika di akhir
                                            pages.push(
                                                <span key="ellipsis1" className="px-1">
                                                    ...
                                                </span>
                                            )
                                            if (currentPage === totalPages - 1) {
                                                pages.push(
                                                    <button
                                                        key={totalPages - 1}
                                                        onClick={() => setCurrentPage(totalPages - 1)}
                                                        className="min-w-[36px] h-9 rounded-full bg-red-600 text-white"
                                                    >
                                                        {totalPages - 1}
                                                    </button>
                                                )
                                            }
                                        }
                                    } else {
                                        // Desktop view logic
                                        if (currentPage <= visiblePages) {
                                            for (let i = 2; i <= visiblePages; i++) {
                                                pages.push(
                                                    <button
                                                        key={i}
                                                        onClick={() => setCurrentPage(i)}
                                                        className={`min-w-[36px] h-9 rounded-full ${currentPage === i
                                                            ? 'bg-red-600 text-white'
                                                            : 'border border-red-600 dark:border-red-500 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                                                            }`}
                                                    >
                                                        {i}
                                                    </button>
                                                )
                                            }
                                            pages.push(
                                                <span key="ellipsis1" className="px-1">
                                                    ...
                                                </span>
                                            )
                                        } else if (currentPage > totalPages - visiblePages) {
                                            pages.push(
                                                <span key="ellipsis1" className="px-1">
                                                    ...
                                                </span>
                                            )
                                            for (let i = totalPages - visiblePages + 1; i < totalPages; i++) {
                                                pages.push(
                                                    <button
                                                        key={i}
                                                        onClick={() => setCurrentPage(i)}
                                                        className={`min-w-[36px] h-9 rounded-full ${currentPage === i
                                                            ? 'bg-red-600 text-white'
                                                            : 'border border-red-600 dark:border-red-500 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                                                            }`}
                                                    >
                                                        {i}
                                                    </button>
                                                )
                                            }
                                        } else {
                                            pages.push(
                                                <span key="ellipsis1" className="px-1">
                                                    ...
                                                </span>
                                            )
                                            for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                                                if (i > 1 && i < totalPages) {
                                                    pages.push(
                                                        <button
                                                            key={i}
                                                            onClick={() => setCurrentPage(i)}
                                                            className={`min-w-[36px] h-9 rounded-full ${currentPage === i
                                                                ? 'bg-red-600 text-white'
                                                                : 'border border-red-600 dark:border-red-500 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                                                                }`}
                                                        >
                                                            {i}
                                                        </button>
                                                    )
                                                }
                                            }
                                            pages.push(
                                                <span key="ellipsis2" className="px-1">
                                                    ...
                                                </span>
                                            )
                                        }
                                    }

                                    // Selalu tampilkan halaman terakhir
                                    if (totalPages > 1) {
                                        pages.push(
                                            <button
                                                key={totalPages}
                                                onClick={() => setCurrentPage(totalPages)}
                                                className={`min-w-[36px] h-9 rounded-full ${currentPage === totalPages
                                                    ? 'bg-red-600 text-white'
                                                    : 'border border-red-600 dark:border-red-500 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                                                    }`}
                                            >
                                                {totalPages}
                                            </button>
                                        )
                                    }
                                }

                                return pages
                            })()}

                            {/* Tombol Next */}
                            <button
                                onClick={nextPage}
                                disabled={currentPage === totalPages}
                                className={`flex items-center px-2 h-9 rounded-full ${currentPage === totalPages
                                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                                    : 'border border-red-600 dark:border-red-500 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                                    }`}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={1.5}
                                    stroke="currentColor"
                                    className="size-4"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            )}
            <Modal
                showModal={isModalOpen}
                selectedReport={selectedReport}
                cancelReason={cancelReason}
                setCancelReason={setCancelReason}
                onClose={handleCloseModal}
                onConfirm={handleSubmitCancel}
                title="Konfirmasi Pembatalan"
                message={`Apakah Anda yakin ingin membatalkan laporan ${selectedReport?.displayId || 'ini'}?`}
                cancelText="Tidak"
                confirmText="Ya, Batalkan"
                showCancelReason={true}
                reasonLabel='Alasan Pembatalan'
                reasonPlaceholder='Masukkan alasan pembatalan...'
            />

            {financePickerModal && ReactDOM.createPortal(
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
                    onClick={() => setFinancePickerModal(null)}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-lg p-4 lg:p-6 max-w-md w-full mx-4 relative transition-colors"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="text-lg md:text-xl font-semibold mb-1 md:mb-2 dark:text-gray-100">
                            Pilih Finance
                        </h2>
                        <p className="mb-3 text-gray-600 dark:text-gray-300 text-sm">
                            Anda terdaftar di lebih dari satu Unit Bisnis. Pilih Finance (Validator) yang akan menerima reminder untuk {financePickerModal.item.displayId}:
                        </p>
                        <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
                            {financePickerModal.candidates.map((validator) => (
                                <button
                                    key={validator.uid}
                                    className="w-full text-left px-4 py-2 rounded-md border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                    onClick={() => handlePickFinance(validator)}
                                >
                                    <div className="font-medium">{validator.nama}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">{validator.unit.join(', ')}</div>
                                </button>
                            ))}
                        </div>
                        <div className="flex justify-end mt-4">
                            <button
                                className="bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-4 py-2 rounded text-sm hover:bg-gray-300 dark:hover:bg-gray-600 hover:text-gray-700 dark:hover:text-gray-100 transition-colors"
                                onClick={() => setFinancePickerModal(null)}
                            >
                                Batal
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    )
}

export default BsTable